import React from "react";
import { WorkflowExecution, ExecutionLogEntry } from "@/types/workflow";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExecutionStatusBadge } from "./ExecutionStatusBadge";
import { ExecutionTimeline } from "./ExecutionTimeline";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { 
  Info, 
  AlertTriangle, 
  XCircle, 
  Bug,
  Clock,
  Workflow,
  FileJson,
  Timer
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ExecutionDetailSheetProps {
  execution: WorkflowExecution | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LogLevelIcon: React.FC<{ level: ExecutionLogEntry["level"] }> = ({ level }) => {
  switch (level) {
    case "info":
      return <Info className="h-4 w-4 text-blue-500" />;
    case "warn":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case "error":
      return <XCircle className="h-4 w-4 text-red-500" />;
    case "debug":
      return <Bug className="h-4 w-4 text-gray-500" />;
  }
};

const logLevelClasses: Record<ExecutionLogEntry["level"], string> = {
  info: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  warn: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  error: "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800",
  debug: "bg-gray-50 border-gray-200 dark:bg-gray-900/30 dark:border-gray-700",
};

// Format duration for display
const formatDurationMs = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

export const ExecutionDetailSheet: React.FC<ExecutionDetailSheetProps> = ({
  execution,
  open,
  onOpenChange,
}) => {
  if (!execution) return null;

  const formatDuration = (startedAt: string, completedAt: string | null) => {
    if (!completedAt) return "Nog actief";
    const durationMs = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  // Check if there are steps with duration
  const hasStepTimings = execution.execution_log?.some(
    entry => entry.duration_ms !== undefined && entry.duration_ms > 0
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5" />
            Execution Details
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-8rem)] mt-4">
          <div className="space-y-6 pr-4">
            {/* Header Info */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">
                  {execution.workflow?.name || "Onbekende workflow"}
                </h3>
                <ExecutionStatusBadge status={execution.status} />
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Execution ID</p>
                  <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                    {execution.id}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Workflow ID</p>
                  <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                    {execution.workflow_id}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>
                    Gestart: {format(new Date(execution.started_at), "dd MMM yyyy HH:mm:ss", { locale: nl })}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <Badge variant="outline">
                  Duur: {formatDuration(execution.started_at, execution.completed_at)}
                </Badge>
                {execution.completed_at && (
                  <Badge variant="outline">
                    Voltooid: {format(new Date(execution.completed_at), "HH:mm:ss", { locale: nl })}
                  </Badge>
                )}
              </div>
            </div>

            {/* Error Message */}
            {execution.error_message && (
              <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium mb-2">
                  <XCircle className="h-4 w-4" />
                  Foutmelding
                </div>
                <p className="text-sm text-red-600 dark:text-red-300 font-mono whitespace-pre-wrap">
                  {execution.error_message}
                </p>
              </div>
            )}

            {/* Step Timeline - NEW */}
            {hasStepTimings && (
              <div className="p-4 bg-muted/30 rounded-lg border">
                <ExecutionTimeline 
                  entries={execution.execution_log}
                  startedAt={execution.started_at}
                  completedAt={execution.completed_at}
                />
              </div>
            )}

            {/* Trigger Data */}
            {execution.trigger_data && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <FileJson className="h-4 w-4" />
                  Trigger Data
                </h4>
                <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(execution.trigger_data, null, 2)}
                </pre>
              </div>
            )}

            {/* Execution Log */}
            <div className="space-y-2">
              <h4 className="font-medium">Execution Log</h4>
              {execution.execution_log && execution.execution_log.length > 0 ? (
                <div className="space-y-2">
                  {execution.execution_log.map((entry, index) => (
                    <div
                      key={index}
                      className={cn(
                        "p-3 rounded-lg border text-sm",
                        logLevelClasses[entry.level]
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <LogLevelIcon level={entry.level} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{entry.message}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              {entry.duration_ms !== undefined && entry.duration_ms > 0 && (
                                <span className={cn(
                                  "text-xs font-mono flex items-center gap-1 px-1.5 py-0.5 rounded",
                                  entry.duration_ms < 1000 && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                                  entry.duration_ms >= 1000 && entry.duration_ms < 5000 && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                                  entry.duration_ms >= 5000 && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                )}>
                                  <Timer className="h-3 w-3" />
                                  {formatDurationMs(entry.duration_ms)}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(entry.timestamp), "HH:mm:ss.SSS")}
                              </span>
                            </div>
                          </div>
                          {entry.node && (
                            <Badge variant="outline" className="text-xs mt-1">
                              Node: {entry.node}
                            </Badge>
                          )}
                          {entry.step_name && (
                            <Badge variant="secondary" className="text-xs mt-1 ml-1">
                              Stap: {entry.step_name}
                            </Badge>
                          )}
                          {entry.data && (
                            <pre className="text-xs mt-2 bg-background/50 p-2 rounded overflow-x-auto">
                              {JSON.stringify(entry.data, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center bg-muted rounded-lg">
                  Geen log entries beschikbaar
                </p>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
